export type ScanImageMime = 'image/jpeg' | 'image/png';
export type ScanArch = 'upper' | 'lower';
export type ScanMeshFormat = 'stl' | 'ply';

export interface ScanMeshEntry {
    path: string;
    arch: ScanArch;
    format: ScanMeshFormat;
}

export type ArchMeshPaths = Partial<Record<ScanArch, string>>;

const IMAGE_MIME_BY_EXTENSION: Readonly<Record<string, ScanImageMime>> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png'
};
const MESH_FORMAT_BY_EXTENSION: Readonly<Record<string, ScanMeshFormat>> = {
    stl: 'stl',
    ply: 'ply'
};
const MAC_METADATA_FOLDER = '__MACOSX';

const ORIENTATION_RANK = { occlusion: 0, unknown: 1, teethup: 2 } as const;
const KIND_RANK = { shell: 0, unknown: 1, abo: 2, solidAbo: 3, lprofile: 4 } as const;
const BITE_RANK = { arch: 0, bite: 1 } as const;
const ARCH_ORDER: readonly ScanArch[] = ['upper', 'lower'];

const nameCollator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

export function baseName(path: string): string {
    const segments = path.split('/');
    return segments[segments.length - 1] ?? path;
}

function extensionOf(path: string): string {
    const name = baseName(path);
    const dot = name.lastIndexOf('.');
    return dot <= 0 ? '' : name.slice(dot + 1).toLowerCase();
}

function stemOf(path: string): string {
    const name = baseName(path).toLowerCase();
    const dot = name.lastIndexOf('.');
    return dot <= 0 ? name : name.slice(0, dot);
}

// Folders, macOS resource forks and dotfiles ride along in hand-made zips and are never content.
export function isIgnoredZipPath(path: string): boolean {
    if (path.endsWith('/')) return true;
    if (path.split('/').includes(MAC_METADATA_FOLDER)) return true;
    return baseName(path).startsWith('.');
}

export function imageMimeType(path: string): ScanImageMime | null {
    if (isIgnoredZipPath(path)) return null;
    return IMAGE_MIME_BY_EXTENSION[extensionOf(path)] ?? null;
}

function compareByFilename(a: string, b: string): number {
    return nameCollator.compare(baseName(a), baseName(b)) || nameCollator.compare(a, b);
}

export function imageEntryPaths(paths: readonly string[]): string[] {
    return paths.filter((path) => imageMimeType(path) !== null).sort(compareByFilename);
}

const ORTHO_ARCH_SUFFIX = /_(u|l)(_bite\d+)?$/;
const DIE_MODEL = /(^|_)die(_|$)/;

export function meshArch(path: string): ScanArch | null {
    const stem = stemOf(path);
    if (DIE_MODEL.test(stem)) return null;
    const suffix = ORTHO_ARCH_SUFFIX.exec(stem)?.[1];
    const isUpper = suffix === 'u' || stem.includes('upper');
    const isLower = suffix === 'l' || stem.includes('lower');
    if (isUpper === isLower) return null;
    return isUpper ? 'upper' : 'lower';
}

function meshFormat(path: string): ScanMeshFormat | null {
    if (isIgnoredZipPath(path)) return null;
    return MESH_FORMAT_BY_EXTENSION[extensionOf(path)] ?? null;
}

export function classifyMeshEntry(path: string): ScanMeshEntry | null {
    const format = meshFormat(path);
    const arch = format ? meshArch(path) : null;
    return format && arch ? { path, arch, format } : null;
}

export function stlMeshPaths(paths: readonly string[]): string[] {
    return paths.filter((path) => meshFormat(path) === 'stl').sort(compareByFilename);
}

export function hasPlyMesh(paths: readonly string[]): boolean {
    return paths.some((path) => meshFormat(path) === 'ply');
}

function kindRank(tokens: readonly string[]): number {
    if (tokens.includes('shell')) return KIND_RANK.shell;
    if (tokens.includes('solid') && tokens.includes('abo')) return KIND_RANK.solidAbo;
    if (tokens.includes('abo')) return KIND_RANK.abo;
    if (tokens.includes('lprofile')) return KIND_RANK.lprofile;
    return KIND_RANK.unknown;
}

function orientationRank(tokens: readonly string[]): number {
    if (tokens.includes('occlusion')) return ORIENTATION_RANK.occlusion;
    if (tokens.includes('teethup')) return ORIENTATION_RANK.teethup;
    return ORIENTATION_RANK.unknown;
}

// Lower sorts first: arches over bite scans, the occlusion pose (both arches line up) over
// teeth-up, then the raw scan shell over the ABO-based study models.
function meshRank(entry: ScanMeshEntry): number[] {
    const tokens = stemOf(entry.path).split(/[_\-\s.]+/);
    const isBite = tokens.some((token) => /^bite\d*$/.test(token));
    return [isBite ? BITE_RANK.bite : BITE_RANK.arch, orientationRank(tokens), kindRank(tokens)];
}

function compareRanks(a: readonly number[], b: readonly number[]): number {
    const index = a.findIndex((value, position) => value !== b[position]);
    return index === -1 ? 0 : a[index] - b[index];
}

function compareMeshEntries(a: ScanMeshEntry, b: ScanMeshEntry): number {
    return compareRanks(meshRank(a), meshRank(b)) || compareByFilename(a.path, b.path);
}

// Only STL competes: the full viewer opens nothing else, so a PLY arch is never preselected.
export function bestArchMeshes(paths: readonly string[]): ArchMeshPaths {
    const candidates = paths
        .map(classifyMeshEntry)
        .filter((entry): entry is ScanMeshEntry => entry?.format === 'stl');
    return ARCH_ORDER.reduce<ArchMeshPaths>((picked, arch) => {
        const best = candidates.filter((entry) => entry.arch === arch).sort(compareMeshEntries)[0];
        return best ? { ...picked, [arch]: best.path } : picked;
    }, {});
}
